import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { applySeo, buildSeo, collectSeo } from '@/lib/seo'

const IS_BROWSER = typeof window !== 'undefined'

/**
 * Sets title, description, canonical, and Open Graph tags for the current
 * route. Replaces the old usePageTitle hook, which set only the title.
 *
 * Distinct titles matter for more than the browser tab: GA4's default "Pages
 * and screens" report groups by page title first, so without this every route
 * reports under one identical title and looks like only the home page is being
 * tracked (the underlying page_path/page_location was always correct, this is a
 * display issue Analytics.tsx's page_title field inherits from document.title).
 *
 * Distinct canonicals matter for Google, which was clustering every route as a
 * duplicate of the home page. See src/lib/seo.ts for the full story.
 *
 * Pass just a section name ("About") to get "About - Christopher Robert
 * White", or omit it on the home page to keep the bare site name.
 */
export function useSeo(title?: string, description?: string) {
  const { pathname } = useLocation()
  const {
    title: pageTitle,
    description: pageDescription,
    canonical,
  } = buildSeo(pathname, title, description)

  // During prerendering there is no DOM and effects never run, so record the
  // metadata as part of the render pass instead. Safe here specifically because
  // the prerenderer does one synchronous renderToString per route.
  if (!IS_BROWSER) {
    collectSeo({ title: pageTitle, description: pageDescription, canonical })
  }

  useEffect(() => {
    applySeo({ title: pageTitle, description: pageDescription, canonical })
  }, [pageTitle, pageDescription, canonical])
}
